/**
 * `W5-T15` §8.2 — the call-out fee as a person reads it, from the server's integers.
 *
 * `shared/format.ts`'s `formatRate` divides cents into a float, which is fine for a listing's guide
 * price and not for a charge: the charter's rule is that no float touches money at any layer. `Intl`
 * also formats an exact decimal **string** (ECMA-402 `StringNumericLiteral`), so the integer is
 * written out as `"<units>.<cents>"` by its digits and formatted from that. Nothing here adds,
 * rounds or recomputes: the total shown is the total the server sent (AC 2).
 */
import { type Money } from '@marketplace/contracts';

export function formatMoney(money: Money, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: money.currency }).format(
    decimal(money.amountCents, 2),
  );
}

/** An IVA rate in basis points (`2100` → *21 %*), the way `BookingSchema.ivaRateBps` carries it. */
export function formatRateBps(bps: number, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 2 }).format(
    decimal(bps, 4),
  );
}

/** `12100, 2` → `"121.00"`; `5, 2` → `"0.05"`. The integer's digits with a point put in. */
function decimal(units: number, places: number): `${number}` {
  const sign = units < 0 ? '-' : '';
  const digits = Math.abs(units)
    .toString()
    .padStart(places + 1, '0');
  return `${sign}${digits.slice(0, -places)}.${digits.slice(-places)}` as `${number}`;
}
